import React, { useEffect, useState, useRef } from 'react';
import { KanaCard } from '../KanaCard';
import { KanaInfo } from './data';
import './MobileKanaSheet.css';

interface MobileKanaSheetProps {
    kanaInfo: KanaInfo | null;
    onClose: () => void;
    displayMode: 'both' | 'hiragana' | 'katakana';
    correspondingKana?: string;
}

export const MobileKanaSheet: React.FC<MobileKanaSheetProps> = ({
    kanaInfo,
    onClose,
    displayMode,
    correspondingKana
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [renderKana, setRenderKana] = useState<KanaInfo | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [currentY, setCurrentY] = useState(0);
    const sheetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (kanaInfo) {
            setRenderKana(kanaInfo);
            // 延迟显示以触发动画
            requestAnimationFrame(() => {
                setIsVisible(true);
            });
            document.body.style.overflow = 'hidden';
        } else {
            setIsVisible(false);
            setCurrentY(0);
            document.body.style.overflow = '';
            // 动画结束后清理 renderKana，但不需要立即清理，以免闪烁
            const timer = setTimeout(() => {
                if (!kanaInfo) {
                    setRenderKana(null);
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [kanaInfo]);

    // 清理副作用
    useEffect(() => {
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        setStartY(touch.clientY);
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return;

        const touch = e.touches[0];
        const deltaY = touch.clientY - startY;

        // 只允许向下拖动
        if (deltaY > 0) {
            setCurrentY(deltaY);
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);

        // 如果拖动超过100px，关闭抽屉
        if (currentY > 100) {
            handleClose();
        } else {
            setCurrentY(0);
        }
    };

    const handleClose = () => {
        setIsVisible(false);
        // 通知父组件关闭，延迟以匹配动画
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // 如果没有要渲染的内容且不可见，则不渲染
    if (!renderKana && !isVisible) return null;

    return (
        <>
            {/* 遮罩层 */}
            <div
                className={`mobile-kana-overlay ${isVisible ? 'visible' : ''}`}
                onClick={handleOverlayClick}
            />

            {/* 底部抽屉 */}
            <div
                ref={sheetRef}
                className={`mobile-kana-sheet ${isVisible ? 'visible' : ''}`}
                style={{
                    transform: isVisible ? `translateY(${currentY}px)` : 'translateY(100%)'
                }}
            >
                {/* 拖动手柄 */}
                <div
                    className="sheet-handle"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="handle-bar" />
                </div>

                {/* 内容区域 */}
                <div className="sheet-content">
                    {renderKana && (
                        <KanaCard
                            kanaInfo={renderKana}
                            onClose={handleClose}
                            displayMode={displayMode}
                            correspondingKana={correspondingKana}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
